// src/components/locutor/locutorConstants.js
// Constantes, cores, ícones e helpers compartilhados entre os módulos do Locutor

export const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3007';

// ============================================
// ÍCONES
// ============================================

export const ICONS = {
  SUCCESS: '✅', ERROR: '❌', WARNING: '⚠️', INFO: 'ℹ️',
  MICROPHONE: '🎙️', PLAY: '▶️', PAUSE: '⏸️', STOP: '⏹️',
  LOCKED: '🔒', UNLOCKED: '🔓', LOADING: '🔄', CLOCK: '⏰',
  VOLUME: '🔊', SPEAKER: '🎛️', MUSIC: '🎵', SETTINGS: '⚙️',
  DICE: '🎲', EDIT: '✏️', TRASH: '🗑️', PLUS: '➕',
  CALENDAR: '📅', TIMER: '⏱️', LIST: '📋', DOWN: '▼', UP: '▲'
};

export const STATUS_ICONS = {
  generating: `${ICONS.LOADING} Gerando...`,
  playing:    `${ICONS.MICROPHONE} Ao vivo`,
  error:      `${ICONS.ERROR} Erro`,
  success:    `${ICONS.SUCCESS} Sucesso`,
  waiting:    `${ICONS.CLOCK} Aguardando`,
  locked:     `${ICONS.LOCKED} Bloqueado`,
  scheduled:  `${ICONS.CLOCK} Agendado`
};

// ============================================
// PALETA DE CORES
// ============================================

export const COLORS = {
  primary:         '#335fc7',
  primaryLight:    '#4c78d9',
  primaryDark:     '#274aa3',
  primaryGradient: 'var(--gradient-brand)',
  successGradient: 'var(--gradient-success)',
  dangerGradient:  'linear-gradient(135deg, var(--error), #9b1a1a)',
  cardBg:          'rgba(var(--brand-rgb), 0.1)',
  overlayBg:       'rgba(var(--brand-rgb), 0.2)',
  hoverBg:         'rgba(var(--brand-rgb), 0.3)',
  border:          'rgba(var(--brand-rgb), 0.3)',
  textPrimary:     '#4c78d9',
  textSecondary:   '#7b9fe8',
  success:         'var(--success)',
  warning:         'var(--warning)',
  danger:          'var(--error)'
};

// ============================================
// PERSISTÊNCIA
// ============================================

export const STORAGE_KEYS = {
  LOCUTOR_STATE:   'radio-ai-locutor-state',
  VOICE_ENGINE:    'radio-ai-voice-engine',
  VOICE_CONFIG:    'radio-ai-voice-config'
};

// ============================================
// VOZES
// ============================================

export const VOICE_OPTIONS = {
  edgetts: {
    label: 'Edge TTS (Grátis)',
    voices: [
      { value: 'pt-BR-AntonioNeural',   label: `${ICONS.MICROPHONE} Antonio (Locutor)` },
      { value: 'pt-BR-FabioNeural',     label: '🎭 Fabio (Apresentador)' },
      { value: 'pt-BR-DonatoNeural',    label: '📻 Donato (Rádio)' },
      { value: 'pt-BR-FranciscaNeural', label: `${ICONS.MICROPHONE} Francisca (Locutora)` },
      { value: 'pt-BR-BrendaNeural',    label: '🎭 Brenda (Animada)' },
      { value: 'pt-BR-LeilaNeural',     label: '📻 Leila (Rádio)' }
    ],
    default: 'pt-BR-AntonioNeural'
  }
};

// ============================================
// INTERVALOS
// ============================================

export const INTERVAL_PRESETS = {
  curto:   { label: 'Curto (3-7 min)',                   ranges: [{ min: 3,  max: 7  }] },
  medio:   { label: 'Médio (7-15 min)',                  ranges: [{ min: 7,  max: 15 }] },
  longo:   { label: 'Longo (30-45 min)',                 ranges: [{ min: 30, max: 45 }] },
  variado: { label: 'Variado (3-11 ou 25-35 min)',       ranges: [{ min: 3,  max: 11 }, { min: 25, max: 35 }] },
  natural: { label: 'Natural (5-12 ou 18-28 min)',       ranges: [{ min: 5,  max: 12 }, { min: 18, max: 28 }] },
};

export const INTERVAL_CONFIG_KEY = 'radio-ai-locutor-interval-config';

// ============================================
// VALIDAÇÃO DE RESPOSTA DE ÁUDIO
// ============================================

export function validateAudioResponse(data, source = 'API') {
  const fail = (msg) => ({ valid: false, message: msg, errors: [msg] });

  if (!data)          return fail(`${ICONS.ERROR} ${source} retornou dados vazios`);
  if (!data.success)  return fail(`${ICONS.ERROR} ${source} retornou success=false: ${data.error || 'erro desconhecido'}`);
  if (!data.audioUrl) return fail(`${ICONS.ERROR} ${source} não retornou audioUrl`);

  let audioUrl = data.audioUrl;
  if (/^https?:\/\//i.test(audioUrl)) {
    // já absoluta
  } else if (audioUrl.startsWith('/')) {
    audioUrl = new URL(audioUrl, API_BASE).href;
  } else {
    audioUrl = new URL(`/${audioUrl}`, API_BASE).href;
  }

  const duration = Number.isFinite(data.duration)
    ? data.duration
    : Number.isFinite(data?.mixDetails?.durationSec)
      ? data.mixDetails.durationSec
      : null;

  return {
    valid:    true,
    message:  `${ICONS.SUCCESS} Resposta válida de ${source}`,
    audioUrl,
    duration,
    fala:     data.fala    || data.text || '',
    pauseMap: data.pauseMap || null,
    bedUrl:   data.bedUrl   || null
  };
}

// ============================================
// HELPERS DE TEMPO
// ============================================

export const DIAS_SEMANA = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];

export function formatHoraFromDate(date) {
  return `${String(date.getHours()).padStart(2,'0')}:${String(date.getMinutes()).padStart(2,'0')}`;
}

export function getPeriodoFromDate(date) {
  const h = date.getHours();
  if (h >= 5  && h < 12) return 'manhã';
  if (h >= 12 && h < 18) return 'tarde';
  if (h >= 18 && h < 24) return 'noite';
  return 'madrugada';
}

export function getDiaSemanaFromDate(date) {
  return DIAS_SEMANA[date.getDay()];
}

export function addSecondsToDate(date, seconds) {
  return new Date(date.getTime() + seconds * 1000);
}

export function formatInterval(ms) {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds === 0 ? `${minutes}min` : `${minutes}min ${seconds}s`;
}

export function formatTimeRemaining(seconds) {
  if (!seconds || seconds <= 0) return 'Agora';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return mins > 0 ? `${mins}min ${secs}s` : `${secs}s`;
}

export function formatTempoRestante(ms) {
  if (!ms || ms <= 0) return 'Expirado';
  const dias    = Math.floor(ms / (24 * 60 * 60 * 1000));
  const horas   = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const minutos = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
  if (dias  > 0) return `${dias}d ${horas}h`;
  if (horas > 0) return `${horas}h ${minutos}min`;
  return `${minutos}min`;
}

export function generateRandomInterval(ranges) {
  if (!ranges || ranges.length === 0) return 7 * 60 * 1000;
  const r = ranges[Math.floor(Math.random() * ranges.length)];
  return Math.round((r.min + Math.random() * (r.max - r.min)) * 60 * 1000);
}

export function loadIntervalConfig() {
  try {
    const saved = localStorage.getItem(INTERVAL_CONFIG_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) {}
  return { preset: 'medio', ranges: INTERVAL_PRESETS.medio.ranges };
}

export function saveIntervalConfig(preset, ranges = null) {
  try {
    const config = { preset, ranges: ranges || INTERVAL_PRESETS[preset].ranges };
    localStorage.setItem(INTERVAL_CONFIG_KEY, JSON.stringify(config));
    return config;
  } catch (e) {
    return null;
  }
}
